import { useCallback, useEffect, useMemo, useState } from 'react';
import { WbsTaskSearchResult } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useCPTLabelSearch } from '../../hooks';
import { DependantLabels } from '../schema';
import { CPT_LABEL_PAGE_COUNT } from '..';

type UseTaskChipData = {
  options: WbsTaskSearchResult[];
  invalidTaskIds: string[];
  handleSearch: (query: string) => void;
  label?: TimesheetLabel | null;
  loading: boolean;
  hasMore: boolean;
};

export const useTasks = (
  dependentLabels: DependantLabels,
  taskChipOpen: boolean
): UseTaskChipData => {
  const [options, setOptions] = useState<WbsTaskSearchResult[]>([]);
  const [lastSearch, setLastSearch] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);

  useEffect(() => {
    setLastSearch(undefined);
  }, [taskChipOpen]);

  const {
    searchHandler,
    data: { task: tasksData }
  } = useCPTLabelSearch();

  const loading = useMemo(() => Boolean(!tasksData), [tasksData]);

  const { client, project, program } = dependentLabels;
  const clientLabelValue = client?.label?.value;
  const projectLabelValue = project?.label?.value;
  const programLabelValue = program?.label?.value;

  const taskDependUri = useMemo(() => {
    if (program) {
      return { programUri: programLabelValue?.idValue };
    } else if (client) {
      return { clientUri: clientLabelValue?.idValue };
    } else {
      return;
    }
  }, [client, clientLabelValue?.idValue, program, programLabelValue?.idValue]);

  const handleSearch = useCallback(
    (queryText: string) => {
      if (lastSearch !== queryText) {
        searchHandler({
          context: 'task',
          query: queryText || undefined,
          ...taskDependUri,
          projectUri: projectLabelValue?.idValue
        });
      }
      setLastSearch(queryText);
    },
    [lastSearch, searchHandler, taskDependUri, projectLabelValue?.idValue]
  );

  useEffect(() => {
    if (loading) {
      setOptions([]);
      setHasMore(false);
    } else if (tasksData) {
      const tasks = tasksData!.wbsLabelValueSearch?.tasks
        .items as WbsTaskSearchResult[];
      setOptions(tasks);
      setHasMore(tasks?.length === CPT_LABEL_PAGE_COUNT);
    }
  }, [tasksData, loading]);

  const { transformedOptions, invalidTaskIds } = useMemo(() => {
    const allTasks = options.map(({ id }) => id);
    const invalidTaskIds: string[] = [];
    const transformedOptions = options.reduce((acc, option) => {
      if (!lastSearch || lastSearch.length === 0) {
        option.parents.forEach((parent, index) => {
          if (!allTasks.includes(parent.id)) {
            invalidTaskIds.push(parent.id);
            acc.push({
              id: parent.id,
              displayText: parent.displayText,
              parents: index !== 0 ? [option.parents[index - 1]] : []
            });
          }
        });
      }
      acc.push(option);

      return acc;
    }, [] as WbsTaskSearchResult[]);

    return { transformedOptions, invalidTaskIds };
  }, [options, lastSearch]);

  return useMemo(
    () => ({
      options: transformedOptions,
      invalidTaskIds: invalidTaskIds,
      label: dependentLabels.task.label,
      handleSearch,
      loading,
      hasMore
    }),
    [
      transformedOptions,
      invalidTaskIds,
      dependentLabels.task,
      handleSearch,
      loading,
      hasMore
    ]
  );
};

export default useTasks;
